import { useTRPC } from "@nwga/features/client";
import { Button } from "@nwga/ui/components/button";
import { Field } from "@nwga/ui/components/field";
import { Input } from "@nwga/ui/components/input";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import * as React from "react";
import { z } from "zod";
import { AuthCard } from "../components/auth-card.tsx";
import { SocialSignIn } from "../components/social-sign-in.tsx";
import { captchaHeaders, useTurnstile } from "../components/turnstile.tsx";
import { env } from "../env.ts";
import { signUp } from "../lib/auth.ts";

/** Mirrors Better Auth's `minPasswordLength` in `packages/auth/src/server.ts`. */
const MIN_PASSWORD_LENGTH = 12;

export const Route = createFileRoute("/sign-up")({
	validateSearch: z.object({
		/** Where to return to afterwards, preserved from a protected-route redirect. */
		redirect: z.string().optional(),
	}),
	component: SignUpPage,
});

function SignUpPage() {
	const { redirect } = Route.useSearch();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const trpc = useTRPC();
	const session = useQuery(trpc.session.queryOptions());

	const [error, setError] = React.useState<string | null>(null);
	const [pending, setPending] = React.useState(false);
	const turnstile = useTurnstile();

	const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setError(null);

		const data = new FormData(event.currentTarget);
		const password = String(data.get("password"));

		if (password.length < MIN_PASSWORD_LENGTH) {
			setError(`Use at least ${MIN_PASSWORD_LENGTH} characters.`);
			return;
		}

		setPending(true);

		const email = String(data.get("email"));
		const result = await signUp.email({
			name: String(data.get("name")),
			email,
			password,
			callbackURL: `${env.webUrl}/verify-email`,
			fetchOptions: { headers: captchaHeaders(turnstile.token) },
		});

		setPending(false);

		if (result.error !== null && result.error !== undefined) {
			// The token just spent is single-use; the retry this error invites needs
			// a fresh one.
			turnstile.reset();
			setError(result.error.message ?? "Could not create your account.");
			return;
		}

		await queryClient.invalidateQueries();

		// With verification required, sign-up does not produce a usable session —
		// sending them to the dashboard would show a signed-out page immediately
		// after a successful sign-up. Check the session rather than assuming
		// either way, since the requirement is configurable.
		const session = await queryClient.fetchQuery(trpc.session.queryOptions());

		if (session.user === null || !session.user.emailVerified) {
			await navigate({ to: "/check-email", search: { email } });
			return;
		}

		await navigate({ to: redirect ?? "/dashboard" });
	};

	return (
		<AuthCard
			title="Create an Account"
			description="Register your business and start posting openings."
			footer={
				<p>
					Already have an account?{" "}
					<Link
						to="/sign-in"
						className="font-medium text-foreground underline-offset-4 hover:underline"
					>
						Sign in
					</Link>
				</p>
			}
		>
			<SocialSignIn
				providers={session.data?.socialProviders ?? []}
				callbackURL={redirect ?? "/signed-in"}
			/>

			<form
				className="mt-4 space-y-4"
				onSubmit={(event) => void handleSubmit(event)}
			>
				<Field label="Name" required>
					{(props) => (
						<Input {...props} name="name" autoComplete="name" required />
					)}
				</Field>

				<Field label="Email" required>
					{(props) => (
						<Input
							{...props}
							name="email"
							type="email"
							autoComplete="email"
							required
						/>
					)}
				</Field>

				<Field
					label="Password"
					required
					description={`At least ${MIN_PASSWORD_LENGTH} characters.`}
					errors={error === null ? undefined : [error]}
				>
					{(props) => (
						<Input
							{...props}
							name="password"
							type="password"
							autoComplete="new-password"
							minLength={MIN_PASSWORD_LENGTH}
							required
						/>
					)}
				</Field>

				{turnstile.widget}

				<Button
					type="submit"
					size="lg"
					className="w-full"
					disabled={pending || turnstile.pending}
				>
					{pending ? "Creating account…" : "Create account"}
				</Button>
			</form>
		</AuthCard>
	);
}
